<template>
  <div v-if="item" class="grid grid-cols-1 sm:grid-cols-2 w-full">
    <div class="w-full">
      <div class="w-full h-[30vh] relative md:fixed md:top-20 md:h-[calc(100vh_-_190px)] md:w-[50%] md:left-0">
        <template v-if="item.attributes.images.length">
          <img :src="item.attributes.images[activeImage]" class="object-cover h-full w-full" />
          <div v-if="item.attributes.images.length > 1" class="absolute top-0 left-0 z-10 w-1/2 h-full flex items-center p-6 cursor-pointer" @click="movePreviousImage">
            <i class="fas fa-chevron-left text-white shadow-sm drop-shadow-lg text-3xl"></i>
          </div>
          <div v-if="item.attributes.images.length > 1" class="absolute top-0 right-0 z-10 w-1/2 h-full flex items-center p-6 cursor-pointer justify-end" @click="moveNextImage">
            <i class="fas fa-chevron-right text-white shadow-sm drop-shadow-lg text-3xl"></i>
          </div>
        </template>
        <template v-else>
          <div class="bg-white h-full w-full flex items-center justify-center">
            <span class="fa-stack text-[72px] opacity-20">
              <i class="fa-image far fa-stack-1x" aria-hidden="true"></i>
              <i class="fa-slash fas icon-stack-base fa-stack-1x " aria-hidden="true"></i>
            </span>
          </div>
        </template>
      </div>
    </div>
    <div class="w-full relative md:min-h-[calc(100vh_-_190px)] p-4 md:pl-40 md:pt-10 md:pr-4">
      <div class="flex justify-end">
        <a :href="item.attributes.urls.back" data-umami-event="Back button" class="text-black no-underline h-4 mb-6 flex items-center gap-2 uppercase text-xs">
          <i class="fas fa-chevron-left"></i>
          {{ i18n.t('general.back_to_shop') }}
        </a>
      </div>
      <div class="text-xl font-bold uppercase mb-2">{{ activeItem.name }}</div>
      <div v-if="item.attributes.delivery" class="text-sm mb-2 flex items-center gap-2">
        <i class="bi bi-truck"></i>{{ i18n.t('item.delivery') }}
      </div>
      <div v-else-if="item.attributes.transport" class="text-sm mb-2 flex items-center gap-2">
        <i class="bi bi-truck"></i>{{ i18n.t('item.transport') }}
      </div>
      <div v-if="selectedVariant" class="uppercase text-sm mb-4">
        <template v-if="selectedVariant.tiered_price">{{ i18n.t('general.from') }}</template>
        {{ selectedVariant.price }}
        {{ item.attributes.currency_symbol }}
        {{ item.attributes.price_unit }}
      </div>
      <div class="flex items-center mb-2 gap-2 text-sm">
        <i class="bi bi-geo-alt"></i>
        {{ item.attributes.address }}
      </div>
      <div class="flex items-center mb-4 gap-2 text-sm">
        <i class="bi bi-shop"></i>
        {{ item.attributes.profile_name }}
      </div>
      <div v-if="hasReviews" class="mb-4 gap-4 text-sm cursor-pointer flex" @click="showReviews = !showReviews">
        <div v-if="item.attributes.google_rating" class="shrink-0">
          <span>
            <i class="bi bi-star-fill text-yellow-400"></i>
            <span class="font-bold">{{ item.attributes.google_rating }}</span>
          </span>
        </div>
        <div>
          <span class="text-xs font-bold cursor-pointer text-blue-600">{{ i18n.t('item.show_reviews') }}</span><br>
          <span class="opacity-50 text-xs text-jt-dark-grey">Powered by Google</span>
        </div>
      </div>
      <div v-if="showReviews" class="flex flex-col gap-2 mb-4 pr-16">
        <div v-for="(review, index) in reviews" :key="index" class="flex gap-2 items-center mb-2 p-2 px-3 rounded bg-white">
          <img :src="review.profile_photo_url" class="w-8 h-8 rounded-full"/>
          <div class="flex flex-col">
            <span class="font-bold text-sm">{{ review.author_name }}</span>
            <div class="flex items-center gap-4">
              <div class="flex items-center gap-1">
                <span class="text-sm font-bold">{{ review.rating }}</span>
                <i class="bi bi-star-fill text-yellow-400"></i>
              </div>
              <span class="text-xs opacity-50">{{ review.relative_time_description }}</span>
            </div>
            <span class="text-sm mt-1">{{ review.text }}</span>
          </div>
        </div>
        <div v-if="reviewsNotLoaded" class="flex justify-center">
          <span class="text-sm opacity-50">{{ i18n.t('item.loading_reviews') }}</span>
        </div>
        <div v-else-if="reviews.length == 0" class="flex justify-center">
          <span class="text-sm opacity-50">{{ i18n.t('item.no_reviews') }}</span>
        </div>
      </div>
      <div
        :class="{
          'block md:hidden': !selectedVariant
        }"
      >
        <div class="flex flex-col mt-4" v-if="variants.length > 1">
          <div class="font-bold mb-2 uppercase">
            {{ i18n.t('item.variants') }}
          </div>
          <div v-for="variant in variants" :key="variant.id" @click="selectedVariant=variant"
               class=" border border-transparent cursor-pointer inline-flex gap-6 items-center p-2 px-3 rounded bg-white shadow mb-2"
               :class="{'font-bold !border-[var(--primary-color)]': selectedVariant == variant}"
          >
            <span class="font-bold">{{ variant.duration || variant.size }}</span>
            {{ variant.name }}
          </div>
        </div>
      </div>
      <div class="flex justify-center md:fixed md:left-[calc(50%_-_125px)] z-[60] md:top-[15%] md:shadow mb-4"
           :class="{'md:left-[calc(50%_-_150px)]': itemAddedToCart, 'md:left-[calc(50%_-_125px)]': !itemAddedToCart}"
       >
        <template v-if="itemAddedToCart">
          <Accessories v-if="item" :item="item" :cart="cart"/>
        </template>
        <template v-else-if="selectedVariant">
          <ItemCalendar v-if="item && !cartExists" :minimumRental="item.attributes.minimum_rental" :key="selectedVariant.id" :availabilityUrl="selectedVariant.availability" :isLesson="item.attributes.itype == 1" @book:item="bookItem" />
          <div v-else class="bg-white rounded-md inline-block relative p-2 w-[250px]">
            <label v-if="item.attributes.itype == 1">
              {{ i18n.t('calendar.start_date') }}
            </label>
            <label v-else>
              {{ i18n.t('general.from') }}
            </label>
            <div class="pb-2">
              {{ cart.start_date }}
            </div>
            <template v-if="!(item.attributes.itype == 1)">
              <label>
                {{ i18n.t('general.to') }}
              </label>
              <div class="pb-4">
                {{ cart.end_date }}
              </div>
            </template>
            <button class="jt-btn w-full"  @click="bookItem">
              {{ i18n.t('item.add_to_cart') }}
            </button>
          </div>
        </template>

        <template v-else>
          <div class="hidden bg-white rounded-md md:inline-block relative p-4">
            <div class="flex flex-col" v-if="variants.length > 1">
              <div class="font-bold mb-2 uppercase">
                {{ i18n.t('item.variants') }}
              </div>
              <div v-for="variant in variants" :key="variant.id" @click="selectedVariant=variant" class="cursor-pointer inline-flex gap-6 items-center p-2 px-3 rounded bg-white shadow mb-2">
                <span class="font-bold">{{ variant.duration || variant.size }}</span>
                {{ variant.name }}
              </div>
            </div>
          </div>
        </template>
      </div>
      <div class="flex items-center gap-4 mb-4 text-sm">
        <div class="cursor-pointer py-2 border border-transparent" :class="{'font-bold border-b-[var(--primary-color)]': activeTab == 'description'}" @click="activeTab = 'description'">
          {{ i18n.t('item.description') }}
        </div>
        <div v-if="item.attributes.components.length" class="cursor-pointer py-2 border border-transparent" :class="{'font-bold border-b-[var(--primary-color)]': activeTab == 'components'}" @click="activeTab = 'components'">
          {{ i18n.t('item.components') }}
        </div>
        <div v-if="item.attributes.tiered_price" class="cursor-pointer py-2 border border-transparent" :class="{'font-bold border-b-[var(--primary-color)]': activeTab == 'pricing'}" @click="activeTab = 'pricing'">
          {{ i18n.t('item.pricing') }}
        </div>
        <div v-if="item.attributes.delivery" class="cursor-pointer py-2 border border-transparent" :class="{'font-bold border-b-[var(--primary-color)]': activeTab == 'delivery'}" @click="activeTab = 'delivery'">
          {{ i18n.t('item.delivery_label') }}
        </div>
        <div v-if="item.attributes.transport" class="cursor-pointer py-2 border border-transparent" :class="{'font-bold border-b-[var(--primary-color)]': activeTab == 'transport'}" @click="activeTab = 'transport'">
          {{ i18n.t('item.transport_label') }}
        </div>
      </div>
      <div v-if="activeTab == 'delivery'" class="text-sm">
        <span class="font-bold">{{ i18n.t('item.d_description') }}:</span> {{ item.attributes.delivery.description }}<br>
        <span class="font-bold">{{ i18n.t('item.d_area') }}:</span> {{ item.attributes.delivery.area }}<br>
        <span class="font-bold">{{ i18n.t('item.d_price') }}:</span> {{ item.attributes.delivery.price }}
      </div>
      <div v-if="activeTab == 'transport'" class="text-sm">
        <span class="font-bold">{{ i18n.t('item.d_description') }}:</span> {{ item.attributes.transport.description }}<br>
        <span class="font-bold">{{ i18n.t('item.d_area') }}:</span> {{ item.attributes.transport.area }}<br>
        <span class="font-bold">{{ i18n.t('item.d_price') }}:</span> {{ item.attributes.transport.price }}
      </div>
      <div v-if="activeTab == 'components'" class="text-sm" >
        <div v-for="(comp ,index) in item.attributes.components" :key="index" class="flex gap-2 items-center mb-2 p-2 px-3 rounded bg-white">
          <span class="font-bold">{{ comp.name }}:</span>
          {{ comp.value.replace(/"/g,'') }}
        </div>
      </div>
      <div v-if="activeTab == 'description' && selectedVariant" class="text-sm" >
        <template v-if="selectedVariant.description.length > 0">
          <div class="flex items-center gap-2 mb-2">
            <span v-if="!showTranslation" @click="showTranslation = true" class="text-xs font-bold cursor-pointer text-blue-600">{{ i18n.t('item.show_translation') }}</span>
            <span v-if="showTranslation" @click="showTranslation = false" class="text-xs font-bold cursor-pointer text-blue-600">{{ i18n.t('item.show_original') }}</span>
            <img src="/images/google_translate.png" class="opacity-50"/>
          </div>
        </template>
        <span v-if="!showTranslation" class="whitespace-pre-line" v-html="selectedVariant.description"></span>
        <span v-if="showTranslation" class="whitespace-pre-line" v-html="descriptionTranslation"></span>
        <div v-if="selectedVariant" class="mt-4 flex items-center gap-6">
          <div v-if="item.attributes.itype == 1" class="py-2">
            <span class="font-bold">{{ i18n.t('general.duration') }}:</span>
            {{ activeItem.duration }}
          </div>
          <div v-if="activeItem.size" class="py-2">
            <span class="font-bold">{{ i18n.t('general.size') }}:</span>
            {{ activeItem.size }}
          </div>
          <div v-if="item.attributes.weight && item.attributes.itype !== 1" class="py-2">
            <span class="font-bold">
              {{ i18n.t('item.weight') }}:
            </span>
            {{ item.attributes.weight }}
          </div>
        </div>
        <div v-if="item.attributes.lesson_data" class="mt-4 flex items-center gap-6">
          <div class="py-2" v-if="item.attributes.lesson_data.difficulty">
            <span class="font-bold">
              {{ i18n.t('item.good_for') }}:
            </span>
            {{ item.attributes.lesson_data.difficulty }}
          </div>
          <div class="py-2" v-if="item.attributes.lesson_data.coach">
            <span class="font-bold">
              {{ i18n.t('item.coach') }}:
            </span>
            {{ item.attributes.lesson_data.coach }}
          </div>
        </div>
        <div v-if="item.attributes.lesson_data" class="mt-4 flex gap-6">
          <div class="py-2" v-if="item.attributes.lesson_data.included.length > 0">
            <div class="font-bold mb-1">
              {{ i18n.t('item.included') }}:
            </div>
            <div v-for="(incl ,index) in item.attributes.lesson_data.included" :key="index" class="flex gap-2 items-center mb-1">
              <i class="fas fa-plus opacity-50"></i>
              {{ incl }}
            </div>
          </div>
          <div class="py-2" v-if="item.attributes.lesson_data.excluded.length > 0">
            <div class="font-bold mb-1">
              {{ i18n.t('item.excluded') }}:
            </div>
            <div v-for="(excl ,index) in item.attributes.lesson_data.excluded" :key="index" class="flex gap-2 items-center mb-1">
              <i class="fas fa-minus opacity-50"></i>
              {{ excl }}
            </div>
          </div>
        </div>
      </div>
      <div v-if="activeTab == 'pricing'" class="text-sm">
        <div v-for="tier in selectedVariant.tiered_prices" :key="item.treshold" class="flex items-center gap-4 mb-2 p-2 px-3 rounded bg-white">
          <div class="font-bold">
            {{ tier.treshold }}
            {{ tier.unit }}
          </div>
          <div class="">
            {{ tier.price }}
            {{ item.attributes.currency_symbol }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>

import ItemCalendar from './calendar.vue'
import Accessories from './accessories.vue'

export default {
  name: 'ItemShowContainer',
  components: {
    ItemCalendar,
    Accessories
  },
  props: {
    itemUrl: String,
    referral: String,
    reviewsUrl: String,
    hasReviews: {
      type: Boolean,
      default: false
    }
  },
  data: function() {
    return {
      reviews: [],
      item: null,
      activeImage: 0,
      activeTab: 'description',
      cartExists: false,
      itemAddedToCart: false,
      selectedVariant: null,
      showTranslation: false,
      descriptionTranslation: null,
      showReviews: false,
      reviewsNotLoaded: true,
    }
  },
  watch: {
    showTranslation() {
      if (this.showTranslation) {
        if (!this.descriptionTranslation) {
          fetch(this.selectedVariant.translated_description_url)
            .then(response => response.json())
            .then(data => {
              this.descriptionTranslation = data.description
            })
        }
      }
    },
    showReviews() {
      if (this.showReviews && this.reviewsNotLoaded) {
        this.fetchReviews()
      }
    },
    selectedVariant() {
      this.descriptionTranslation = null;
      this.showTranslation = false;
    }
  },
  mounted() {
    this.getItem();
    if (this.referral) {
      window.referralCode = this.referral;
      localStorage.setItem('referralCode', this.referral);
    }
  },
  created() {
    this.checkCartState();
  },
  computed: {
    cart() {
      return window.topNavigation?.$refs.topNavigation.cart.attributes
    },
    variants() {
      return this.item.attributes.variants
    },
    activeItem() {
      return this.selectedVariant || this.item.attributes
    }
  },
  methods: {
    getItem() {
      fetch(this.itemUrl)
        .then(response => response.json())
        .then(data => {
          this.item = data.data
          if (this.variants.length == 1) {
            this.selectedVariant = this.variants[0]
          }
        })
    },
    fetchReviews() {
      fetch(this.reviewsUrl)
        .then(response => response.json())
        .then(data => {
          this.reviews = data
          this.reviewsNotLoaded = false
        })
    },
    moveNextImage() {
      this.activeImage += 1
      if (this.activeImage > this.item.attributes.images.length - 1) {
        this.activeImage = 0
      }
    },
    movePreviousImage() {
      this.activeImage -= 1
      if (this.activeImage < 0) {
        this.activeImage = this.item.attributes.images.length - 1
      }
    },
    bookItem(period) {
      if (period) {
        window.topNavigation?.$refs.topNavigation.updateCart(
          this.selectedVariant.id,
          1,
          {
            profile_id: this.item.attributes.profile_id,
            start_date: period.from,
            end_date: period.to
          }
        )
      } else {
        window.topNavigation?.$refs.topNavigation.updateCart(
          this.selectedVariant.id,
          1
        )
      }
    },
    checkCartState() {
      if (window.topNavigation?.$refs.topNavigation?.cart) {
        this.cartExists = true
        if (this.item) {
          this.itemAddedToCart = window.topNavigation?.$refs.topNavigation.cartItems.find(item => item.attributes.item_id == this.selectedVariant?.id)
        }
      } else {
        this.cartExists = false
        this.itemAddedToCart = false
      }
      setTimeout(() => {
       this.checkCartState()
      }, 300)
    }
  }
}
</script>
